// Çalışanın bordro sayfası (emp-payroll.jsx "5a"): tek sayfada dönem seçici,
// seçili dönemin bordro zarfı ve başlıkta PDF indirme. Ekranda kalem dökümü
// yoktur (KVKK); döküm yalnız PDF'tedir. Dönem toplamı (net) gösterilebilir,
// kalemler gösterilemez.
//
// Tek veri kaynağı GET /v1/payroll-tr/me/statements: satır zaten çalışanın o
// koşudaki tek bordrosunu (pay_statement_id) taşır. Önceki iki adımlı akış
// ayrıca /runs/:runId/statements okuyordu; o uç kesinleşmemiş koşuyu da
// döndürüyordu ve PDF ucu onu zaten 404 ile reddediyordu.
//
// /me/payroll en yeni dönemi açar (liste sunucuda en yeni önce sıralı);
// /me/payroll/:runId aynı bileşendir ve dönemi URL'den seçer.
//
// Çizimde olup arka ucu olmayanlar bilinçli olarak yok: "E-postaya gönder",
// ödeme tarihi, banka/IBAN satırı ve sicil alt başlığı.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { Download, FileText, Lock } from "lucide-react";

import {
  Button,
  Card,
  CardBody,
  Empty,
  ErrorState,
  Loading,
  PageHeader,
} from "@novemai/components";

import { errorMessage } from "@/api/errors";
import { useKeysetQuery } from "@/api/keyset";
import { Money } from "@/components/Money";
import { formatDateRange } from "@/lib/date-range";
import {
  downloadPayslipPdf,
  fetchMyPeriodsPage,
  type MyPeriodRow,
} from "@/features/payroll/payroll-api";
import { formatRunPeriod } from "@/features/payroll/period";
import { PayslipActivity } from "@/features/payroll/payslip-notices-panel";
import { payslipFileName } from "@/features/payroll/payslip-file-name";

export function PayslipContent(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const runId = useParams()["runId"];

  const periods = useKeysetQuery<MyPeriodRow>({
    queryKey: ["payroll-tr", "my-periods"],
    fetchPage: (cursor) => fetchMyPeriodsPage(cursor),
  });
  const selected =
    runId === undefined
      ? periods.items[0]
      : periods.items.find((row) => row.pay_run_id === runId);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = periods;
  // Eski bir döneme verilen bağlantı ilk sayfada olmayabilir; satır bulunana ya
  // da liste bitene kadar sayfalamaya devam edilir.
  const seeking = runId !== undefined && selected === undefined && hasNextPage;
  useEffect(() => {
    if (seeking && !isFetchingNextPage) fetchNextPage();
  }, [seeking, isFetchingNextPage, fetchNextPage]);

  const [downloading, setDownloading] = useState(false);
  const [failedFor, setFailedFor] = useState<string | null>(null);
  const onDownload = async (row: MyPeriodRow): Promise<void> => {
    setDownloading(true);
    setFailedFor(null);
    try {
      await downloadPayslipPdf(
        row.pay_statement_id,
        payslipFileName({ prefix: t("payroll.payslip.pdf_filename"), periodStart: row.period_start }),
      );
    } catch {
      setFailedFor(row.pay_statement_id);
    } finally {
      setDownloading(false);
    }
  };

  const periodName = selected === undefined ? null : formatRunPeriod(i18n.language, selected);

  return (
    <div className="mx-auto max-w-[1100px] space-y-6">
      <PageHeader
        title={
          <>
            {t("payroll.payslip.title")} <em>{t("payroll.payslip.title_em")}</em>
          </>
        }
        description={
          periodName === null
            ? t("payroll.payslip.lead")
            : t("self_payroll.payslip.lead", { period: periodName })
        }
        actions={
          selected === undefined ? undefined : (
            <Button
              variant="primary"
              loading={downloading}
              onClick={(): void => {
                void onDownload(selected);
              }}
            >
              <Download size={16} aria-hidden="true" />
              {t("payroll.payslip.download_pdf")}
            </Button>
          )
        }
      />

      {periods.isLoading || seeking ? (
        <Loading label={t("payroll.payslip.periods_loading")} rows={3} />
      ) : periods.isError ? (
        <ErrorState
          title={t("payroll.payslip.periods_error_title")}
          description={errorMessage(periods.error, t)}
        />
      ) : periods.items.length === 0 ? (
        <Empty
          title={t("payroll.payslip.no_period_title")}
          description={t("payroll.payslip.no_period_description")}
        />
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
          {selected === undefined ? (
            <Empty
              title={t("self_payroll.payslip.not_found_title")}
              description={t("self_payroll.payslip.not_found_description")}
              action={
                <Button asChild variant="secondary">
                  <Link to="/me/payroll">{t("self_payroll.payslip.open_latest")}</Link>
                </Button>
              }
            />
          ) : (
            <PayslipEnvelope
              row={selected}
              periodName={periodName ?? selected.period_label}
              downloadFailed={failedFor === selected.pay_statement_id}
            />
          )}
          <PeriodSelector
            items={periods.items}
            selectedRunId={selected?.pay_run_id ?? null}
            hasNextPage={periods.hasNextPage}
            isFetchingNextPage={periods.isFetchingNextPage}
            onLoadMore={periods.fetchNextPage}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Dönem seçici: her dönem kendi derin bağlantısıdır. Seçim `push` ile
 * gezinir (Link varsayılanı); dönemler ayrı görünümlerdir ve Geri tuşu önceki
 * döneme dönmelidir. Varsayılan seçim (parametresiz) URL'yi yeniden yazmaz.
 */
function PeriodSelector({
  items,
  selectedRunId,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: {
  items: readonly MyPeriodRow[];
  selectedRunId: string | null;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}): React.ReactElement {
  const { t, i18n } = useTranslation();
  return (
    <Card className="self-start overflow-hidden" data-tour="payroll-periods">
      <nav aria-label={t("self_payroll.payslip.periods_nav_label")}>
        <div className="border-b border-[var(--border-subtle)] px-4 py-3">
          <h2 className="font-serif text-[17px] text-[var(--fg-primary)]">
            {t("self_payroll.payslip.periods_title")}
          </h2>
        </div>
        <ul className="flex flex-col gap-1 p-2">
          {items.map((row) => {
            const current = row.pay_run_id === selectedRunId;
            return (
              <li key={row.pay_statement_id}>
                <Link
                  to={`/me/payroll/${row.pay_run_id}`}
                  aria-current={current ? "page" : undefined}
                  className={
                    current
                      ? "flex min-h-11 items-center justify-between gap-3 rounded-[9px] border border-[var(--accent)] bg-[var(--accent-subtle-bg)] px-3 py-2.5"
                      : "flex min-h-11 items-center justify-between gap-3 rounded-[9px] border border-transparent px-3 py-2.5 hover:bg-[var(--bg-subtle)]"
                  }
                >
                  <span className="min-w-0">
                    <span
                      className={
                        current
                          ? "block font-sans text-[13px] font-semibold text-[var(--accent-active)]"
                          : "block font-sans text-[13px] font-semibold text-[var(--fg-primary)]"
                      }
                    >
                      {formatRunPeriod(i18n.language, row)}
                    </span>
                    <span className="block font-sans text-[11px] text-[var(--fg-muted)]">
                      {formatDateRange(i18n.language, row.period_start, row.period_end)}
                    </span>
                  </span>
                  <span className="shrink-0 font-sans text-[12px] text-[var(--fg-secondary)]">
                    <Money minorUnits={Number(row.net_pay_minor_units)} currency={row.currency} />
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
        {hasNextPage && (
          <div className="flex justify-center px-2 pb-3">
            <Button
              variant="secondary"
              loading={isFetchingNextPage}
              disabled={isFetchingNextPage}
              onClick={onLoadMore}
            >
              {t("payroll.payslip.periods_load_more")}
            </Button>
          </div>
        )}
      </nav>
    </Card>
  );
}

/**
 * Seçili dönemin bordro zarfı. İndirme düğmesi sayfa başlığındadır; burada
 * ikinci bir birincil düğme yoktur. Hazır olma durumu sunucunundur: liste yalnız
 * kesinleşmiş dönemleri döndürür, PDF ucu erişimi her istekte yeniden denetler.
 */
function PayslipEnvelope({
  row,
  periodName,
  downloadFailed,
}: {
  row: MyPeriodRow;
  periodName: string;
  downloadFailed: boolean;
}): React.ReactElement {
  const { t, i18n } = useTranslation();
  return (
    <Card className="overflow-hidden" data-tour="payslip-envelope">
      <div className="bg-[var(--accent-subtle-bg)] px-6 py-5 text-[var(--accent-active)]">
        <div className="font-sans text-[11px] font-semibold uppercase tracking-[0.06em] opacity-70">
          {t("payroll.payslip.envelope_label")}
        </div>
        <h2 className="mt-1.5 font-serif text-[26px]">{periodName}</h2>
        <dl className="mt-1.5 flex flex-wrap gap-x-6 gap-y-1 font-serif text-[14px]">
          <div className="flex gap-1.5">
            <dt className="sr-only">{t("self_payroll.payslip.period_dates")}</dt>
            <dd>{formatDateRange(i18n.language, row.period_start, row.period_end)}</dd>
          </div>
          <div className="flex gap-1.5">
            <dt>{t("self_payroll.payslip.net_pay")}</dt>
            <dd className="font-semibold">
              <Money minorUnits={Number(row.net_pay_minor_units)} currency={row.currency} />
            </dd>
          </div>
        </dl>
      </div>

      <CardBody className="text-center">
        <div className="relative mx-auto mb-5 flex h-[108px] w-[88px] flex-col items-center justify-center rounded-[10px] border border-[var(--border-subtle)] bg-[var(--bg-subtle)]">
          <FileText size={30} className="text-[var(--accent)]" aria-hidden="true" />
          <div className="mt-1.5 font-sans text-[10px] font-semibold tracking-[0.04em] text-[var(--fg-muted)]">
            {t("payroll.payslip.pdf_doc_label")}
          </div>
        </div>
        <div className="font-serif text-[20px] text-[var(--fg-primary)]">
          {t("payroll.payslip.ready_banner_title")}
        </div>
        <p className="mx-auto mt-2.5 max-w-[520px] font-serif text-[14px] leading-[1.6] text-[var(--fg-secondary)]">
          {t("payroll.payslip.ready_banner_body")}
        </p>
        {downloadFailed && (
          <p role="alert" className="mt-3 font-sans text-[12.5px] text-[var(--state-danger)]">
            {t("payroll.payslip.download_failed")}
          </p>
        )}
        <div className="mt-4"><PayslipActivity statementId={row.pay_statement_id} selfOnly /></div>
        <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-[var(--border-subtle)] bg-[var(--bg-subtle)] px-3.5 py-[7px] font-sans text-[11.5px] text-[var(--fg-muted)]">
          <Lock size={12} aria-hidden="true" />
          {t("payroll.payslip.secure_pill")}
        </div>
      </CardBody>
    </Card>
  );
}
